<template>
    <div class="three-sixty">
        <LoadingAnimation v-if="isLoading" />

        <Image
            v-for="(image, index) in images"
            v-show="!isLoading && index === currentImageIndex"
            :key="index"
            :height="416"
            :width="740"
            fit="contain"
            loading="eager"
            v-bind="
                getGenericImageProps({
                    size: image.size,
                    size_x2: image.size_x2,
                })
            "
            :alt="altGetter(index)"
            @error="() => onload(image.url)"
            @load="() => onload(image.url)"
        />

        <div class="input-catcher-wrapper">
            <div
                ref="inputCatcherEl"
                class="input-catcher"
                @mousedown="handleMouseDown"
                @touchstart="handleTouchStart"
                @touchmove.prevent.stop="handleTouchMove"
            >
                <div class="scrolled-content"></div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs, watch } from 'vue'

import { useThreeSixtyImages } from './useThreeSixtyImages'
import { useThreeSixtyInput } from './useThreeSixtyInput'
import type { ThreeSixtyFragment } from '@/graphql/api'
import { getGenericImageProps } from '@/components/useImageProps'

import Image from '@/components/Image.vue'
import LoadingAnimation from '@/components/ui/LoadingAnimation.vue'

interface ThreeSixtyProps {
    images: ThreeSixtyFragment[]
    initialImageIndex?: number
    externalImageIndex?: number
    altGetter: (index: number) => string
}

const props = withDefaults(defineProps<ThreeSixtyProps>(), {
    initialImageIndex: 0,
    externalImageIndex: undefined,
})

const emit = defineEmits<{
    'index': [number]
}>()

const { images } = toRefs(props)
const loadedImages = ref<Record<string, true>>({})

const onload = (url: string) => {
    loadedImages.value[url] = true
}

const isLoading = computed(() =>
    images.value.some((item) => !(item.url in loadedImages.value))
)

const { currentImageIndex } = useThreeSixtyImages(
    images,
    props.initialImageIndex
)

watch(currentImageIndex, (idx) => {
    emit('index', idx)
})

watch(
    () => props.externalImageIndex,
    (n) => {
        if (undefined !== n && n !== currentImageIndex.value) {
            currentImageIndex.value = n
        }
    }
)

const { handleTouchStart, handleTouchMove, handleMouseDown, inputCatcherEl } =
    useThreeSixtyInput(currentImageIndex)
</script>

<style lang="scss" scoped>
.three-sixty {
    position: relative;
    max-width: 740px;
    width: 100%;
    height: 416px;
    user-select: none;
    cursor: grab;
    display: flex;
    align-items: center;

    :deep(.image) {
        mix-blend-mode: darken;
    }

    .input-catcher-wrapper {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;
    }

    .input-catcher {
        position: absolute;
        top: 0;
        left: 0;
        width: 110%;
        height: 110%;
        overflow: scroll;

        .scrolled-content {
            width: 4000%;
        }
    }
}
</style>
